import { am5 } from '@local/finsera-core/src/libs';
import { _ } from '@local/finsera-core/src/libs';
import * as types from '@local/finsera-core/src/types';

const getTriangle = (root: am5.Root, fill: am5.Color, outline = false, size = 1, isLegend = false) => {
  return am5.Triangle.new(root, {
    width: 10 * size,
    height: 10 * size,
    fill: outline ? fill : am5.color(0xffffff),
    stroke: fill,
    strokeWidth: outline ? 2 : undefined,
    centerX: isLegend ? 0 : am5.p50,
    centerY: isLegend ? 0 : am5.p50,
  });
};

const getCircle = (root: am5.Root, fill: am5.Color, outline = false, size = 1, isLegend = false) => {
  return am5.Circle.new(root, {
    radius: 5 * size,
    fill: outline ? am5.color(0xffffff) : fill,
    stroke: fill,
    strokeWidth: outline ? 2 : undefined,
    centerX: isLegend ? 0 : am5.p50,
    centerY: isLegend ? 0 : am5.p50,
  });
};

const getSquare = (root: am5.Root, fill: am5.Color, outline = false, size = 1, isLegend = false) => {
  return am5.Rectangle.new(root, {
    width: (outline ? 8 : 10) * size,
    height: (outline ? 8 : 10) * size,
    fill: outline ? am5.color(0xffffff) : fill,
    stroke: fill,
    strokeWidth: outline ? 2 : undefined,
    centerX: isLegend ? 0 : am5.p50,
    centerY: isLegend ? 0 : am5.p50,
  });
};

const shapes = [
  {
    getShape: (root: am5.Root, fill: am5.Color, size: number, isLegend = false) =>
      getCircle(root, fill, false, size, isLegend),
    fill: am5.color('#80aced'),
  },
  {
    getShape: (root: am5.Root, fill: am5.Color, size: number, isLegend = false) =>
      getCircle(root, fill, false, size, isLegend),
    fill: am5.color('#53a66a'),
  },
  {
    getShape: (root: am5.Root, fill: am5.Color, size: number, isLegend = false) =>
      getCircle(root, fill, true, size, isLegend),
    fill: am5.color('#6189C4'),
  },
  {
    getShape: (root: am5.Root, fill: am5.Color, size: number, isLegend = false) =>
      getSquare(root, fill, false, size, isLegend),
    fill: am5.color('#6189C4'),
  },
  {
    getShape: (root: am5.Root, fill: am5.Color, size: number, isLegend = false) =>
      getSquare(root, fill, true, size, isLegend),
    fill: am5.color('#6189C4'),
  },
  {
    getShape: (root: am5.Root, fill: am5.Color, size: number, isLegend = false) =>
      getTriangle(root, fill, false, size, isLegend),
    fill: am5.color('#6189C4'),
  },
  {
    getShape: (root: am5.Root, fill: am5.Color, size: number, isLegend = false) =>
      getTriangle(root, fill, true, size, isLegend),
    fill: am5.color('#6189C4'),
  },
];

export const getCategories = ({
  data,
  category,
  weightField,
  root,
}: {
  data: types.widgets.TableData;
  category: string;
  weightField?: string;
  root: am5.Root;
}) => {
  return _.uniq(data.map((item) => item[category])).map((categoryValue, idx) => {
    // Get data for this category
    const categoryData = data.filter((item) => item[category] === categoryValue);
    let singleValue = false;

    // Calculate min and max weights for this category if weightField is provided
    let minWeight = 1;
    let maxWeight = 1;
    let normalizeWeight = (_w: number) => 1;

    if (weightField) {
      const weights = categoryData.map((item) => Number(item[weightField])).filter((w) => !isNaN(w));
      if (weights.length > 0) {
        minWeight = Math.min(...weights);
        maxWeight = Math.max(...weights);

        // Create normalization function (scales between 0.5 and 2)
        const range = maxWeight - minWeight;

        // Check if all weights are the same before creating the normalization function
        if (range === 0) {
          singleValue = true;
          normalizeWeight = () => 2.5; // If all weights are the same, use max size + 0.5
        } else {
          normalizeWeight = (w: number) => {
            return 0.5 + (1.5 * (w - minWeight)) / range;
          };
        }
      }
    }

    return {
      category: categoryValue,
      color: shapes[idx % shapes.length].fill,
      getShape: (size: number, isLegend: boolean) =>
        shapes[idx % shapes.length].getShape(root, shapes[idx % shapes.length].fill, size, isLegend),
      categoryData,
      singleValue,
      normalizeWeight,
    };
  });
};
